import { useState } from 'react';
import type { SocialPostResult } from '../types';
import { SOCIAL_PLATFORMS } from '../types';
import { SocialPostCard, CopyAllButton, HashtagChip } from './SocialPostCard';
import SocialGenerationProgress from './SocialGenerationProgress';
import EditImagePanel, { type EditHistoryEntry } from '@/components/feature/EditImagePanel';
import { downloadFile } from '@/lib/download';
import { getImageStyleConfig } from '@/config/imageStyles';

interface SocialPostResultsProps {
  result: SocialPostResult | null;
  isGenerating: boolean;
  platform: string;
  businessName: string;
  imageStyle?: string;
  onRegenerate: () => void;
}

function ResultSection({
  icon,
  title,
  badge,
  accentBg,
  accentText,
  children,
  action,
}: {
  icon: string;
  title: string;
  badge?: string;
  accentBg: string;
  accentText: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`w-6 h-6 flex items-center justify-center rounded-md ${accentBg}`}>
          <i className={`${icon} ${accentText} text-xs`} />
        </div>
        <h3 className="text-xs font-bold text-stone-800 uppercase tracking-widest">{title}</h3>
        {badge && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accentBg} ${accentText}`}>
            {badge}
          </span>
        )}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  );
}

function PlatformMockup({
  platform,
  businessName,
  caption,
  imageUrl,
}: {
  platform: string;
  businessName: string;
  caption: string;
  imageUrl: string | null;
}) {
  const platformInfo = SOCIAL_PLATFORMS.find((p) => p.id === platform);
  const avatarLetter = businessName.charAt(0).toUpperCase();

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden max-w-sm mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white text-sm font-bold shrink-0">
          {avatarLetter}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-stone-900 truncate">{businessName}</div>
          <div className="flex items-center gap-1 text-xs text-stone-400">
            <i className={`${platformInfo?.icon ?? 'ri-global-line'} text-xs`} />
            {platform}
          </div>
        </div>
        <i className="ri-more-fill text-stone-300 text-lg" />
      </div>

      {/* Image */}
      {imageUrl && (
        <div className="w-full aspect-square bg-stone-100 overflow-hidden">
          <img
            src={imageUrl}
            alt="Generated social post visual"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Caption preview */}
      <div className="px-4 py-3">
        <p className="text-xs text-stone-700 leading-relaxed line-clamp-4">{caption}</p>
      </div>

      {/* Engagement bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-stone-100">
        <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
          <i className="ri-heart-line text-sm" /> Like
        </div>
        <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
          <i className="ri-chat-1-line text-sm" /> Comment
        </div>
        <div className="flex items-center gap-1.5 text-stone-400 text-xs font-medium">
          <i className="ri-share-forward-line text-sm" /> Share
        </div>
      </div>
    </div>
  );
}

function CtaChip({ cta }: { cta: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(cta);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
        copied
          ? 'bg-green-500 border-green-500 text-white'
          : 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100'
      }`}
    >
      {copied ? <i className="ri-check-line text-xs" /> : <i className="ri-cursor-line text-xs" />}
      {cta}
    </button>
  );
}

function CtaList({ ctas }: { ctas: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {ctas.map((cta, idx) => (
        <CtaChip key={idx} cta={cta} />
      ))}
    </div>
  );
}

export default function SocialPostResults({
  result,
  isGenerating,
  platform,
  businessName,
  imageStyle,
  onRegenerate,
}: SocialPostResultsProps) {
  const imageStyleConfig = imageStyle ? getImageStyleConfig(imageStyle) : null;
  const [activeCaption, setActiveCaption] = useState<1 | 2>(1);
  const [copiedAll, setCopiedAll] = useState(false);
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);

  // When result changes (new generation), reset edit state
  const activeImageUrl = editedImageUrl ?? result?.imageUrl ?? null;

  const handleImageEdited = (newUrl: string) => {
    if (!originalImageUrl && result?.imageUrl) {
      setOriginalImageUrl(result.imageUrl);
    }
    setEditedImageUrl(newUrl);
  };

  const handleRevertToOriginal = () => {
    setEditedImageUrl(null);
    setEditHistory([]);
    setOriginalImageUrl(null);
  };

  const handleDownloadImage = () => {
    if (!activeImageUrl) return;
    downloadFile(activeImageUrl, `social-post-image-${Date.now()}.jpg`);
  };

  const handleCopyAll = () => {
    if (!result) return;
    const parts = [
      `=== CONTENT ANGLE ===\n${result.contentAngle || '—'}`,
      `=== PRIMARY HOOK ===\n${result.primaryHook || result.hooks[0] || '—'}`,
      `=== CAPTION OPTION 1 ===\n${result.caption1}`,
      `=== CAPTION OPTION 2 ===\n${result.caption2}`,
      `=== RECOMMENDED CTA ===\n${result.recommendedCta || result.ctaSuggestions[0] || '—'}`,
      `=== HOOKS ===\n${result.hooks.map((h, i) => `${i + 1}. ${h}`).join('\n')}`,
      `=== CTA SUGGESTIONS ===\n${result.ctaSuggestions.map((c, i) => `${i + 1}. ${c}`).join('\n')}`,
      `=== HASHTAGS ===\n${result.hashtagSuggestions.join(' ')}`,
      `=== VISUAL DIRECTION ===\n${result.visualDirection}`,
      `=== SHORT-FORM VIDEO IDEA ===\n${result.shortVideoIdea || '—'}`,
      `=== PLATFORM VARIATIONS ===\nFacebook: ${result.platformVariations.facebook || '—'}\nInstagram: ${result.platformVariations.instagram || '—'}\nLinkedIn: ${result.platformVariations.linkedin || '—'}`,
    ];
    navigator.clipboard.writeText(parts.join('\n\n'));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2500);
  };

  /* Loading */
  if (isGenerating) {
    return <SocialGenerationProgress mode="post" platform={platform} />;
  }

  if (isGenerating && !isGenerating) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5">
        <div className="relative">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-400">
            <i className="ri-sparkling-2-fill text-white text-2xl" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white flex items-center justify-center">
            <span className="w-3 h-3 rounded-full bg-orange-400 animate-ping" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-base font-bold text-stone-900 mb-1">Writing your social post...</p>
          <p className="text-sm text-stone-400">
            Crafting captions, hooks &amp; hashtags for {platform}
          </p>
        </div>
        <div className="flex gap-1.5 mt-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <p className="text-xs text-stone-400 max-w-xs text-center px-4">
          This usually takes about 15–25 seconds
        </p>
      </div>
    );
  }

  /* Empty */
  if (!result) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5 px-8 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-orange-50 border border-orange-100">
          <i className="ri-sparkling-2-fill text-orange-400 text-3xl" />
        </div>
        <div>
          <p className="text-lg font-bold text-stone-800 mb-2">Your social post will appear here</p>
          <p className="text-sm text-stone-400 max-w-xs leading-relaxed">
            Fill in your details on the left and click{' '}
            <strong className="text-stone-600">Generate Social Post</strong> to get started.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {[
            '2 caption options with different angles',
            'Hook variations to grab attention',
            'CTA suggestions that convert',
            'Platform-optimized hashtags',
            'Visual direction & image concept',
          ].map((item) => (
            <div key={item} className="flex items-center gap-2.5 text-sm text-stone-400">
              <i className="ri-check-line text-orange-400 shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const displayCaption = activeCaption === 1 ? result.caption1 : result.caption2;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-stone-100 flex items-center justify-between shrink-0 gap-2">
        <div className="min-w-0 shrink">
          <h2 className="text-sm md:text-base font-bold text-stone-900">Social Post</h2>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <p className="text-xs text-stone-400 truncate">
              {platform} · <strong className="text-stone-600">{businessName}</strong> ·{' '}
              {result.postType}
            </p>
            {imageStyleConfig && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600 text-xs font-semibold whitespace-nowrap shrink-0">
                <i className="ri-palette-line text-xs" />
                {imageStyleConfig.label}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleCopyAll}
            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-2 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              copiedAll
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-300'
            }`}
          >
            <i className={`${copiedAll ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
            <span className="hidden sm:inline">{copiedAll ? 'Copied!' : 'Copy All'}</span>
          </button>
          <button
            onClick={onRegenerate}
            className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-2.5 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-xs" />
            <span className="hidden sm:inline">Regenerate</span>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto">
        <div className="px-4 md:px-6 py-4 md:py-6 space-y-8">

          {/* ── Mockup Preview ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-md bg-orange-100">
                <i className="ri-smartphone-line text-orange-500 text-xs" />
              </div>
              <h3 className="text-xs font-bold text-stone-800 uppercase tracking-widest">
                Post Preview
              </h3>
              {/* Caption toggle */}
              <div className="ml-auto flex items-center bg-stone-100 rounded-full p-1 gap-0.5">
                <button
                  onClick={() => setActiveCaption(1)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCaption === 1 ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  Caption 1
                </button>
                <button
                  onClick={() => setActiveCaption(2)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCaption === 2 ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  Caption 2
                </button>
              </div>
            </div>
            <PlatformMockup
              platform={platform}
              businessName={businessName}
              caption={displayCaption}
              imageUrl={activeImageUrl}
            />
          </div>

          {/* ── Caption Options ── */}
          {(result.contentAngle || result.primaryHook) && (
            <ResultSection
              icon="ri-compass-3-line"
              title="Content Angle"
              accentBg="bg-violet-100"
              accentText="text-violet-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.contentAngle && (
                  <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
                    <p className="text-xs font-semibold text-violet-700 uppercase tracking-wider mb-1.5">
                      Strategic Angle
                    </p>
                    <p className="text-sm text-stone-700 leading-relaxed">{result.contentAngle}</p>
                  </div>
                )}
                {(result.primaryHook || result.hooks[0]) && (
                  <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">
                      Recommended Hook
                    </p>
                    <p className="text-sm text-stone-700 leading-relaxed">{result.primaryHook || result.hooks[0]}</p>
                  </div>
                )}
              </div>
            </ResultSection>
          )}

          {/* ── Caption Options ── */}
          <ResultSection
            icon="ri-chat-quote-line"
            title="Caption Options"
            badge="2 variations"
            accentBg="bg-orange-100"
            accentText="text-orange-600"
            action={<CopyAllButton items={[result.caption1, result.caption2]} label="Captions" />}
          >
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
                    Option 1
                  </span>
                  <span className="text-xs text-stone-400">Different angle</span>
                </div>
                <SocialPostCard
                  label="Caption 1"
                  content={result.caption1}
                  icon="ri-chat-quote-line"
                  accentBg="bg-orange-100"
                  accentText="text-orange-600"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
                    Option 2
                  </span>
                  <span className="text-xs text-stone-400">Alternative approach</span>
                </div>
                <SocialPostCard
                  label="Caption 2"
                  content={result.caption2}
                  icon="ri-chat-quote-line"
                  accentBg="bg-amber-100"
                  accentText="text-amber-600"
                />
              </div>
            </div>
          </ResultSection>

          {/* ── Hooks ── */}
          {result.hooks.length > 0 && (
            <ResultSection
              icon="ri-flashlight-fill"
              title="Hook Options"
              badge={`${result.hooks.length} variations`}
              accentBg="bg-amber-100"
              accentText="text-amber-600"
              action={<CopyAllButton items={result.hooks} label="Hooks" />}
            >
              <div className="flex flex-col gap-2">
                {result.hooks.map((hook, idx) => (
                  <SocialPostCard
                    key={idx}
                    label="Hook"
                    content={hook}
                    icon="ri-flashlight-fill"
                    accentBg="bg-amber-100"
                    accentText="text-amber-600"
                    index={idx}
                  />
                ))}
              </div>
            </ResultSection>
          )}

          {/* ── CTA Suggestions ── */}
          {result.ctaSuggestions.length > 0 && (
            <ResultSection
              icon="ri-cursor-line"
              title="CTA Suggestions"
              badge={`${result.ctaSuggestions.length} options`}
              accentBg="bg-teal-100"
              accentText="text-teal-600"
            >
              <div className="space-y-3">
                {result.recommendedCta && (
                  <div className="rounded-xl border border-teal-100 bg-teal-50 p-4">
                    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider mb-1.5">
                      Recommended CTA
                    </p>
                    <p className="text-sm font-semibold text-stone-700">{result.recommendedCta}</p>
                  </div>
                )}
                <CtaList ctas={result.ctaSuggestions} />
              </div>
            </ResultSection>
          )}

          {/* ── Hashtags ── */}
          {result.hashtagSuggestions.length > 0 && (
            <ResultSection
              icon="ri-hashtag"
              title="Hashtag Suggestions"
              badge={`${result.hashtagSuggestions.length} tags`}
              accentBg="bg-stone-100"
              accentText="text-stone-600"
              action={
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(result.hashtagSuggestions.join(' '));
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-stone-200 bg-stone-50 text-stone-500 hover:border-orange-300 hover:text-orange-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-copy-line text-xs" />
                  Copy All Tags
                </button>
              }
            >
              <div className="flex flex-wrap gap-2">
                {result.hashtagSuggestions.map((tag, idx) => (
                  <HashtagChip key={idx} tag={tag} />
                ))}
              </div>
            </ResultSection>
          )}

          {/* ── Visual Direction ── */}
          {result.visualDirection && (
            <ResultSection
              icon="ri-image-line"
              title="Visual Direction"
              accentBg="bg-stone-100"
              accentText="text-stone-600"
            >
              <div className="rounded-xl border border-stone-100 bg-stone-50 p-4">
                <p className="text-sm text-stone-600 leading-relaxed">{result.visualDirection}</p>
                {result.imageConcept && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                      Image Concept
                    </p>
                    <p className="text-xs text-stone-500 leading-relaxed font-mono bg-white rounded-lg p-3 border border-stone-100">
                      {result.imageConcept}
                    </p>
                  </div>
                )}
              </div>
            </ResultSection>
          )}

          {/* ── Short-form Video Idea ── */}
          {result.shortVideoIdea && (
            <ResultSection
              icon="ri-video-line"
              title="Short-form Video Idea"
              accentBg="bg-rose-100"
              accentText="text-rose-700"
            >
              <div className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                <p className="text-sm text-stone-700 leading-relaxed">{result.shortVideoIdea}</p>
              </div>
            </ResultSection>
          )}

          {/* ── Platform Variations ── */}
          {(result.platformVariations.facebook || result.platformVariations.instagram || result.platformVariations.linkedin) && (
            <ResultSection
              icon="ri-repeat-line"
              title="Platform Variations"
              accentBg="bg-sky-100"
              accentText="text-sky-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(['facebook', 'instagram', 'linkedin'] as const).map((channel) => {
                  const copy = result.platformVariations[channel];
                  if (!copy) return null;
                  const label = channel.charAt(0).toUpperCase() + channel.slice(1);
                  return (
                    <div key={channel} className="rounded-xl border border-sky-100 bg-sky-50 p-4">
                      <p className="text-xs font-semibold text-sky-700 uppercase tracking-wider mb-1.5">
                        {label}
                      </p>
                      <p className="text-sm text-stone-700 leading-relaxed">{copy}</p>
                    </div>
                  );
                })}
              </div>
            </ResultSection>
          )}

          {/* ── Generated Image ── */}
          {result.imageUrl && (
            <ResultSection
              icon="ri-image-line"
              title="Generated Image"
              accentBg="bg-orange-100"
              accentText="text-orange-600"
            >
              <div className="space-y-3">
                {/* Image display */}
                <div className="rounded-xl overflow-hidden border border-stone-200">
                  <img
                    src={activeImageUrl ?? result.imageUrl}
                    alt="Generated social post image"
                    className="w-full aspect-square object-cover"
                  />
                  <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-stone-100">
                    <div className="flex items-center gap-2 flex-wrap">
                      {imageStyleConfig ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded overflow-hidden border border-stone-200 shrink-0">
                            <img
                              src={imageStyleConfig.previewImage}
                              alt={imageStyleConfig.label}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <span className="text-xs font-semibold text-stone-600">
                            {imageStyleConfig.label}
                          </span>
                          <span className="text-xs text-stone-400">style</span>
                        </div>
                      ) : (
                        <span className="text-xs text-stone-400">
                          {editedImageUrl ? 'AI-edited image' : 'AI-generated image for your post'}
                        </span>
                      )}
                      {editedImageUrl && (
                        <span className="text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-full">
                          Edited
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleDownloadImage}
                      className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line text-xs" />
                      Download
                    </button>
                  </div>
                </div>

                {/* Edit Image Panel */}
                <EditImagePanel
                  imageUrl={activeImageUrl ?? result.imageUrl}
                  imageKey={result.imageUrl}
                  onImageEdited={handleImageEdited}
                  editHistory={editHistory}
                  onHistoryUpdate={setEditHistory}
                  onRevertToOriginal={editedImageUrl ? handleRevertToOriginal : undefined}
                  hasEdit={!!editedImageUrl}
                  creativeIndex={0}
                />
              </div>
            </ResultSection>
          )}
        </div>
      </div>
    </div>
  );
}
